import { pickReferences } from '@/data/heroRecords';
import { seedMobileFor } from '@/data/seedPhone';
import type {
  Booking,
  ConfirmationTouch,
  CustomerRecord,
  Deposit,
  MedicalProfile,
  Piece,
} from '@/domain/types';

/**
 * Prompt 6 — a self-contained matrix of bookings that exercises every state the
 * BookingDetailCard can render (status, deposit state, confirmation stage,
 * derived flags, collab, piece continuity, actual-vs-scheduled, invoice status,
 * reschedule history, AI-summary fallbacks). Each row is opened from the
 * /showcase screen so every permutation can be walked without hunting the
 * calendar.
 *
 * Everything lives on dedicated `cust-show-*` customers so it never pollutes the
 * derived state of the hero customers used elsewhere. Time-agnostic rows are
 * parked ~45 days out to keep the default calendar day clean; only the rows whose
 * derivation depends on the sim clock (in-chair, actual-vs-scheduled) are
 * anchored near "now".
 */

export interface ShowcaseEntry {
  id: string;
  category: string;
  label: string;
}

interface Row {
  category: string;
  label: string;
  booking: Booking;
  deposit?: Deposit;
  piece?: Piece;
  /** Sibling bookings/pieces needed for the derivation (not listed in the manifest). */
  extraBookings?: Booking[];
}

const SHOWCASE_ANCHOR_ISO = '2026-05-04T08:42:00+10:00';

function medical(over: Partial<MedicalProfile>): MedicalProfile {
  return {
    lastUpdatedAt: '2026-04-28T10:00:00+10:00',
    lastUpdatedBy: 'Front desk',
    ...over,
  };
}

export function seedDetailShowcase(nowIso: string): {
  bookings: Booking[];
  deposits: Deposit[];
  pieces: Piece[];
  customers: CustomerRecord[];
  manifest: ShowcaseEntry[];
} {
  const now = new Date(nowIso).getTime();
  const MIN = 60_000;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  const iso = (ms: number) => new Date(ms).toISOString();
  const minsAgo = (n: number) => iso(now - n * MIN);
  const hoursAgo = (n: number) => iso(now - n * HOUR);
  const daysAgo = (n: number) => iso(now - n * DAY);
  const FAR = now + 45 * DAY;
  const farAt = (dayOffset: number, hour: number) => {
    const d = new Date(FAR + dayOffset * DAY);
    d.setHours(hour, 0, 0, 0);
    return d.toISOString();
  };
  // Three back-to-back 4h slots per parked day (09:00 / 13:00 / 17:00) so two
  // rows landing on the same artist and day never overlap — the matrix is walked
  // by id from /showcase, so exact dates don't matter, only that chairs are clean.
  let slot = 0;
  const nextFar = () => farAt(Math.floor(slot / 3), 9 + (slot++ % 3) * 4);
  // The three otherwise-"live" in-chair permutations render on /showcase (walked by
  // booking id, date-agnostic) but must not appear on the real Coburg floor /
  // calendar for the sim day - a showcase card is not an operational session. Park
  // them in the far-future showcase region like the other rows (nextFar() hands out
  // a fresh, non-overlapping chair slot, so they never collide with a real seeded
  // sitting), and express each row's check-in as an offset from that scheduled start
  // so the started-late / early-check-in / in-chair derivations still read right.
  const farLive = (checkedInOffsetMin: number) => {
    const startMs = new Date(nextFar()).getTime();
    const at = iso(startMs + checkedInOffsetMin * MIN);
    return { start: iso(startMs), checkedInAt: at, actualStart: at };
  };

  // --- Customers -------------------------------------------------------------
  const mkCustomer = (
    id: string,
    firstName: string,
    lastName: string,
    over: Partial<CustomerRecord> = {},
  ): CustomerRecord => ({
    id,
    firstName,
    lastName,
    phone: seedMobileFor(57, id),
    ageBand: '25-34',
    suburb: 'Coburg',
    postcode: '3058',
    state: 'VIC',
    distanceToNearestStudioKm: 4,
    nearestStudioId: 'coburg',
    primaryStudioId: 'coburg',
    source: { channel: 'ig-dm', firstTouchAt: '2026-03-01T10:00:00+10:00' },
    leadScore: 72,
    leadStage: 'booked',
    toneTag: 'warm-familiar',
    engagementStatus: 'active',
    conversation: [],
    rapportNotes: [],
    futureReadinessSignals: [],
    bookings: [],
    pieces: [],
    deposits: [],
    creditOnFile: { balanceAud: 0, history: [] },
    crossStudioTouches: [],
    doNotContact: false,
    lifetimeSpendAud: 1_800,
    ...over,
  });

  const customers: CustomerRecord[] = [
    mkCustomer('cust-show-nadia', 'Nadia', 'Rossi'),
    mkCustomer('cust-show-theo', 'Theo', 'Nkemelu'),
    mkCustomer('cust-show-deacon', 'Deacon', 'Yi'),
    mkCustomer('cust-show-harriet', 'Harriet', 'Vo'),
    mkCustomer('cust-show-liv', 'Liv', 'Carter'),
    mkCustomer('cust-show-mason', 'Mason', 'Carter'),
    mkCustomer('cust-show-priya', 'Priya', 'Anand', {
      medicalProfile: medical({
        allergies: ['latex', 'red pigment'],
        notes: 'Reacts to red ink — patch-test any colour work.',
      }),
    }),
    mkCustomer('cust-show-georgia', 'Georgia', 'Wills', {
      medicalProfile: medical({
        conditions: ['pregnant', 'blood-thinner'],
        medications: ['warfarin'],
        notes: 'On blood thinners — GP clearance on file. Shorter sessions.',
      }),
    }),
  ];

  // --- Builders --------------------------------------------------------------
  const mkBooking = (id: string, over: Partial<Booking> = {}): Booking => ({
    id,
    customerId: 'cust-show-nadia',
    studioId: 'coburg',
    artistId: 'pat',
    start: nextFar(),
    durationMin: 240,
    sessionLength: 'half-day',
    status: 'confirmed',
    designBrief: 'Fine-line black-and-grey — showcase permutation.',
    summary: 'Fine-line black-and-grey forearm piece — showcase permutation demo.',
    bodyPart: 'forearm',
    bodyPartSide: 'right',
    colour: 'black-and-grey',
    styleTags: ['fine-line'],
    refsLocation: 'uploaded',
    dayRateAud: 1_200,
    referencePhotos: pickReferences('black-and-grey', 2, id),
    source: 'agent-initiated-chat',
    createdAt: daysAgo(12),
    ...over,
  });

  const mkDeposit = (
    id: string,
    bookingId: string,
    customerId: string,
    over: Partial<Deposit> = {},
  ): Deposit => ({
    id,
    customerId,
    studioId: 'coburg',
    artistId: 'pat',
    bookingId,
    amountAud: 400,
    feesAud: 12,
    netAud: 388,
    status: 'paid',
    channelSent: 'sms',
    stripePaymentLinkId: `plink_show_${id}`,
    expiresAt: iso(now + 30 * DAY),
    createdAt: daysAgo(11),
    paidAt: daysAgo(10),
    policy: 'seven-day-rule',
    source: 'agent-initiated-chat',
    ...over,
  });

  const touch = (
    at: string,
    channel: ConfirmationTouch['channel'],
    outcome: ConfirmationTouch['outcome'],
    by: string,
  ): ConfirmationTouch => ({ at, channel, outcome, by });

  const rows: Row[] = [];
  const push = (r: Row) => rows.push(r);

  // ── Status (one per status) ────────────────────────────────────────────────
  push({ category: 'Status', label: 'held', booking: mkBooking('bk-show-status-held', { status: 'held', dayRateAud: undefined }) });
  push({ category: 'Status', label: 'pending-deposit', booking: mkBooking('bk-show-status-pending', { customerId: 'cust-show-theo', status: 'pending-deposit', dayRateAud: undefined }) });
  {
    const b = mkBooking('bk-show-status-confirmed', { status: 'confirmed', depositId: 'dep-show-status-confirmed' });
    push({ category: 'Status', label: 'confirmed', booking: b, deposit: mkDeposit('dep-show-status-confirmed', b.id, b.customerId) });
  }
  {
    // Checked in but not yet seated: no booth assigned. Uses the default far-future
    // slot (like held/pending/confirmed) so it never collides with a live chair.
    const b = mkBooking('bk-show-status-checkedin', { status: 'checked-in', dayRateAud: undefined });
    push({ category: 'Status', label: 'checked-in', booking: { ...b, checkedInAt: b.start } });
  }
  push({
    category: 'Status',
    label: 'in-chair',
    booking: mkBooking('bk-show-status-inchair', {
      customerId: 'cust-show-theo',
      artistId: 'brandon',
      status: 'in-chair',
      // Parked in the far-future showcase region (off the live Coburg floor);
      // checked in 5m after the scheduled start.
      ...farLive(5),
      createdAt: daysAgo(9),
      boothNumber: 2,
    }),
  });
  // A moved booking. 'rescheduled' is no longer a status (W4-D): a move keeps
  // the lifecycle status and records the move in rescheduleHistory, exactly as
  // migration 20260924120000 converts legacy rows. Slug kept for stable ids.
  // The booking sits where its last move landed (mkBooking still draws its
  // default slot, so every later row keeps its parked slot).
  {
    const movedTo = nextFar();
    push({
      category: 'Status',
      label: 'confirmed after a move',
      booking: mkBooking('bk-show-status-rescheduled', {
        artistId: 'brandon',
        status: 'confirmed',
        start: movedTo,
        rescheduleHistory: [
          { fromIso: farAt(0, 9), toIso: movedTo, reason: 'client-move', by: 'studio-manager', at: daysAgo(3) },
        ],
      }),
    });
  }
  push({
    category: 'Status',
    label: 'completed',
    booking: mkBooking('bk-show-status-completed', {
      customerId: 'cust-show-theo',
      artistId: 'brandon',
      status: 'completed',
      start: daysAgo(3),
      createdAt: daysAgo(12),
      checkedInAt: daysAgo(3),
      checkedOutAt: iso(new Date(daysAgo(3)).getTime() + 4 * HOUR),
      invoiceStatus: 'paid',
      amountPaidAud: 1_200,
      paymentMethod: 'card',
      paidAt: daysAgo(3),
      rebookOutcome: { kind: 'noRebookDepositApplied', reason: 'needs-to-think' },
      rebookAttribution: { facilitatedBy: 'artist', facilitatedByStaffId: 'brandon', capturedAt: daysAgo(3) },
    }),
  });
  push({ category: 'Status', label: 'cancelled', booking: mkBooking('bk-show-status-cancelled', { artistId: 'connor', status: 'cancelled', dayRateAud: undefined }) });
  push({ category: 'Status', label: 'no-show', booking: mkBooking('bk-show-status-noshow', { customerId: 'cust-show-theo', artistId: 'connor', status: 'no-show', dayRateAud: undefined }) });

  // ── Deposit state ───────────────────────────────────────────────────────────
  push({ category: 'Deposit', label: 'none on file', booking: mkBooking('bk-show-dep-none', { customerId: 'cust-show-deacon' }) });
  {
    const b = mkBooking('bk-show-dep-partial', { customerId: 'cust-show-deacon', depositId: 'dep-show-partial' });
    push({
      category: 'Deposit',
      label: 'partial (top-up pending)',
      booking: b,
      deposit: mkDeposit('dep-show-partial', b.id, b.customerId, {
        status: 'pending',
        amountPaid: 200,
        ledger: [{ at: daysAgo(10), amountAud: 200, kind: 'paid', by: 'agent-cameron', note: 'Half deposit — top-up due' }],
      }),
    });
  }
  {
    const b = mkBooking('bk-show-dep-full', { customerId: 'cust-show-deacon', depositId: 'dep-show-full' });
    push({
      category: 'Deposit',
      label: 'fully paid',
      booking: b,
      deposit: mkDeposit('dep-show-full', b.id, b.customerId, {
        ledger: [{ at: daysAgo(10), amountAud: 400, kind: 'paid', by: 'agent-cameron' }],
      }),
    });
  }
  {
    const b = mkBooking('bk-show-dep-rollover', { customerId: 'cust-show-harriet', depositId: 'dep-show-rollover' });
    push({
      category: 'Deposit',
      label: 'rolled-over-in',
      booking: b,
      deposit: mkDeposit('dep-show-rollover', b.id, b.customerId, {
        ledger: [
          { at: daysAgo(18), amountAud: 200, kind: 'rollover-in', by: 'system', note: 'Rolled from prior session' },
          { at: daysAgo(10), amountAud: 200, kind: 'paid', by: 'agent-cameron' },
        ],
      }),
    });
  }
  {
    const b = mkBooking('bk-show-dep-forfeit', { customerId: 'cust-show-harriet', status: 'cancelled', depositId: 'dep-show-forfeit', dayRateAud: undefined });
    push({
      category: 'Deposit',
      label: 'forfeited (7-day cancel)',
      booking: b,
      deposit: mkDeposit('dep-show-forfeit', b.id, b.customerId, {
        ledger: [
          { at: daysAgo(9), amountAud: 400, kind: 'paid', by: 'agent-cameron' },
          { at: daysAgo(1), amountAud: 400, kind: 'forfeit', by: 'system', note: 'Cancelled inside the 7-day window' },
        ],
      }),
    });
  }
  {
    const b = mkBooking('bk-show-dep-split', { customerId: 'cust-show-liv', depositId: 'dep-show-split' });
    push({
      category: 'Deposit',
      label: 'split across customers',
      booking: b,
      deposit: mkDeposit('dep-show-split', b.id, b.customerId, {
        splitWithCustomerIds: ['cust-show-mason'],
        ledger: [{ at: daysAgo(10), amountAud: 400, kind: 'paid', by: 'agent-cameron', note: 'Couple booking — split 50/50' }],
      }),
    });
  }

  // ── Confirmation stage ───────────────────────────────────────────────────────
  const confRows: Array<{ stage: Booking['confirmationStage']; touches: ConfirmationTouch[]; label: string }> = [
    { stage: 'pre-7d', label: 'pre-7d', touches: [] },
    { stage: '7d-text-sent', label: '7d-text-sent', touches: [touch(daysAgo(7), 'sms', 'no-response', 'auto-studio')] },
    { stage: '7d-call-pending', label: '7d-call-pending', touches: [touch(daysAgo(7), 'sms', 'no-response', 'auto-studio')] },
    { stage: '7d-call-done', label: '7d-call-done', touches: [touch(daysAgo(7), 'sms', 'no-response', 'auto-studio'), touch(daysAgo(6), 'call', 'left-vm', 'agent-cameron')] },
    { stage: '2d-text-sent', label: '2d-text-sent', touches: [touch(daysAgo(7), 'sms', 'confirmed', 'auto-studio'), touch(daysAgo(2), 'sms', 'no-response', 'auto-studio')] },
    { stage: 'confirmed', label: 'confirmed', touches: [touch(daysAgo(7), 'sms', 'no-response', 'auto-studio'), touch(daysAgo(2), 'sms', 'confirmed', 'auto-studio')] },
    { stage: 'unreachable', label: 'unreachable', touches: [touch(daysAgo(7), 'sms', 'no-response', 'auto-studio'), touch(daysAgo(6), 'call', 'no-response', 'agent-cameron'), touch(daysAgo(2), 'sms', 'no-response', 'auto-studio')] },
  ];
  for (const c of confRows) {
    push({
      category: 'Confirmation',
      label: c.label,
      booking: mkBooking(`bk-show-conf-${c.stage}`, { confirmationStage: c.stage, confirmationTouches: c.touches }),
    });
  }

  // ── Derived flags ─────────────────────────────────────────────────────────────
  push({ category: 'Flag', label: 'medical-note (allergies)', booking: mkBooking('bk-show-flag-medical', { customerId: 'cust-show-priya' }) });
  push({ category: 'Flag', label: 'medical-note (pregnancy / blood-thinner)', booking: mkBooking('bk-show-flag-medical-2', { customerId: 'cust-show-georgia' }) });
  {
    const b = mkBooking('bk-show-flag-topup', { customerId: 'cust-show-deacon', depositId: 'dep-show-flag-topup' });
    push({
      category: 'Flag',
      label: 'deposit-topup-pending',
      booking: b,
      deposit: mkDeposit('dep-show-flag-topup', b.id, b.customerId, {
        status: 'pending',
        amountPaid: 250,
        ledger: [{ at: daysAgo(10), amountAud: 250, kind: 'paid', by: 'agent-cameron' }],
      }),
    });
  }
  {
    // late-fill: booked <48h before the slot AND a same-artist cancellation freed a
    // nearby slot in the prior 7 days. Self-contained on chicha's book. Parked on a
    // reserved far day (40) so it can't collide with the sequential nextFar() tiles.
    const lateStart = farAt(40, 12);
    push({
      category: 'Flag',
      label: 'late-fill',
      booking: mkBooking('bk-show-flag-latefill', {
        artistId: 'chicha',
        start: lateStart,
        createdAt: iso(new Date(lateStart).getTime() - 12 * HOUR),
      }),
      extraBookings: [
        mkBooking('bk-show-flag-latefill-cancelled', {
          artistId: 'chicha',
          status: 'cancelled',
          start: iso(new Date(lateStart).getTime() - 1 * DAY),
          createdAt: farAt(-1, 10),
          dayRateAud: undefined,
        }),
      ],
    });
  }

  // ── Collab ────────────────────────────────────────────────────────────────────
  push({ category: 'Collab', label: 'one collab artist', booking: mkBooking('bk-show-collab-one', { collabArtistIds: ['brandon'] }) });
  push({ category: 'Collab', label: 'three-way collab', booking: mkBooking('bk-show-collab-three', { collabArtistIds: ['brandon', 'connor'] }) });

  // ── Piece continuity ────────────────────────────────────────────────────────
  push({ category: 'Piece', label: 'solo (no piece)', booking: mkBooking('bk-show-piece-solo', { pieceId: undefined }) });

  const pieces: Piece[] = [];
  {
    // Session 2 of 6: one prior completed sitting + this one, linked to a 6-session piece.
    const prior = mkBooking('bk-show-piece-s26-s1', { customerId: 'cust-show-nadia', pieceId: 'piece-show-s26', status: 'completed', start: daysAgo(30), createdAt: daysAgo(40), checkedInAt: daysAgo(30), checkedOutAt: daysAgo(30), invoiceStatus: 'paid' });
    const b = mkBooking('bk-show-piece-s26', { customerId: 'cust-show-nadia', pieceId: 'piece-show-s26' });
    pieces.push({
      id: 'piece-show-s26',
      customerId: 'cust-show-nadia',
      title: 'Japanese half-sleeve',
      designType: 'sleeve',
      placement: 'left arm',
      style: 'irezumi',
      referencePhotos: pickReferences('sleeve', 3, 'piece-show-s26'),
      assignedArtistId: 'pat',
      estimatedTotalHours: 24,
      hoursCompleted: 4,
      estimatedTotalSessions: 6,
      sessionsCompleted: 1,
      status: 'in-progress',
      bookingsLinked: [prior.id, b.id],
      lastSessionDate: daysAgo(30),
    });
    push({ category: 'Piece', label: 'session 2 of 6', booking: b, extraBookings: [prior] });
  }
  {
    // Final session: 2-session piece, 1 completed → this is the final sitting.
    const prior = mkBooking('bk-show-piece-final-s1', { customerId: 'cust-show-nadia', pieceId: 'piece-show-final', status: 'completed', start: daysAgo(25), createdAt: daysAgo(35), checkedInAt: daysAgo(25), checkedOutAt: daysAgo(25), invoiceStatus: 'paid' });
    const b = mkBooking('bk-show-piece-final', { customerId: 'cust-show-nadia', pieceId: 'piece-show-final' });
    pieces.push({
      id: 'piece-show-final',
      customerId: 'cust-show-nadia',
      title: 'Neck script',
      designType: 'lettering',
      placement: 'neck',
      style: 'script lettering',
      referencePhotos: pickReferences('lettering', 2, 'piece-show-final'),
      assignedArtistId: 'pat',
      estimatedTotalHours: 8,
      // The prior sitting is a 240min half-day, so the piece is 4h in, not 3.
      hoursCompleted: 4,
      estimatedTotalSessions: 2,
      sessionsCompleted: 1,
      status: 'in-progress',
      bookingsLinked: [prior.id, b.id],
      lastSessionDate: daysAgo(25),
    });
    push({ category: 'Piece', label: 'final session (2 of 2)', booking: b, extraBookings: [prior] });
  }
  {
    // Piece with a pricingPlan so the new-booking form's expected-day-rate demo works.
    const b = mkBooking('bk-show-piece-plan', { customerId: 'cust-show-harriet', pieceId: 'piece-show-plan' });
    pieces.push({
      id: 'piece-show-plan',
      customerId: 'cust-show-harriet',
      title: 'Colour back-piece',
      designType: 'colour-realism',
      placement: 'upper back',
      style: 'colour realism',
      referencePhotos: pickReferences('colour-realism', 3, 'piece-show-plan'),
      assignedArtistId: 'pat',
      estimatedTotalHours: 30,
      hoursCompleted: 4,
      estimatedTotalSessions: 5,
      sessionsCompleted: 1,
      status: 'in-progress',
      bookingsLinked: [b.id],
      lastSessionDate: daysAgo(20),
      pricingPlan: [
        { sessionIndex: 1, expectedDayRate: 900, sessionKind: 'start' },
        { sessionIndex: 2, expectedDayRate: 1_000, sessionKind: 'continuation' },
        { sessionIndex: 3, expectedDayRate: 1_000, sessionKind: 'continuation' },
        { sessionIndex: 4, expectedDayRate: 1_000, sessionKind: 'continuation' },
        { sessionIndex: 5, expectedDayRate: 800, sessionKind: 'final' },
      ],
    });
    push({ category: 'Piece', label: 'pricing plan (new-booking demo)', booking: b });
  }
  {
    // Piece with sessionRefs keyed to a specific booking (piece refs + session refs).
    const b = mkBooking('bk-show-piece-sessionrefs', { customerId: 'cust-show-harriet', pieceId: 'piece-show-sessionrefs' });
    pieces.push({
      id: 'piece-show-sessionrefs',
      customerId: 'cust-show-harriet',
      title: 'Blackwork thigh',
      designType: 'blackwork',
      placement: 'thigh',
      style: 'ornamental blackwork',
      referencePhotos: pickReferences('blackwork', 3, 'piece-show-sessionrefs'),
      sessionRefs: { [b.id]: pickReferences('blackwork', 2, 'piece-show-sessionrefs-session') },
      assignedArtistId: 'pat',
      estimatedTotalHours: 12,
      hoursCompleted: 2,
      estimatedTotalSessions: 3,
      sessionsCompleted: 1,
      status: 'in-progress',
      bookingsLinked: [b.id],
      lastSessionDate: daysAgo(15),
    });
    push({ category: 'Piece', label: 'session refs stacked', booking: b });
  }

  // ── Actual vs scheduled ──────────────────────────────────────────────────────
  push({
    category: 'Actual',
    label: 'started 30m late',
    booking: mkBooking('bk-show-actual-late', {
      customerId: 'cust-show-theo',
      artistId: 'chicha',
      status: 'in-chair',
      // Far-future showcase region (off the live Coburg floor); started 30m after
      // the scheduled slot.
      ...farLive(30),
      createdAt: daysAgo(9),
      boothNumber: 4,
      rebookOutcome: { kind: 'rebookedInChair', newBookingId: 'bk-show-prompt-roll-future', depositRolledForward: false },
      rebookAttribution: { facilitatedBy: 'artist', facilitatedByStaffId: 'pat', capturedAt: hoursAgo(1) },
    }),
  });
  push({
    category: 'Actual',
    label: 'early check-in',
    booking: mkBooking('bk-show-actual-early', {
      artistId: 'coburg-marlowe',
      status: 'in-chair',
      // Far-future showcase region (off the live Coburg floor); checked in 80m
      // before the scheduled start (early check-in).
      ...farLive(-80),
      createdAt: daysAgo(9),
      boothNumber: 5,
      rebookOutcome: { kind: 'rebookedInChair', newBookingId: 'bk-show-prompt-roll-future', depositRolledForward: false },
      rebookAttribution: { facilitatedBy: 'artist', facilitatedByStaffId: 'pat', capturedAt: minsAgo(15) },
    }),
  });
  push({
    category: 'Actual',
    label: 'ran shorter than scheduled',
    booking: mkBooking('bk-show-actual-short', {
      customerId: 'cust-show-theo',
      status: 'completed',
      start: daysAgo(2),
      createdAt: daysAgo(12),
      durationMin: 240,
      checkedInAt: daysAgo(2),
      checkedOutAt: iso(new Date(daysAgo(2)).getTime() + 150 * MIN),
      invoiceStatus: 'paid',
      amountPaidAud: 1_200,
      paymentMethod: 'card',
      paidAt: daysAgo(2),
      rebookOutcome: { kind: 'noRebookDepositApplied', reason: 'going-overseas' },
      rebookAttribution: { facilitatedBy: 'artist', facilitatedByStaffId: 'pat', capturedAt: daysAgo(2) },
    }),
  });
  push({ category: 'Actual', label: 'no check-in yet', booking: mkBooking('bk-show-actual-none') });

  // ── Invoice status ────────────────────────────────────────────────────────────
  // Each on its own past day (daysAgo 5..9) so the five completed sittings don't
  // stack on Pat's chair at one time. 'issued' (idx 2) is bumped to daysAgo(10)
  // instead of daysAgo(7): the 7-days-ago slot lands on 2026-04-27 local, the
  // same day + overlapping window as the harper-nguyen journey's firm
  // bk-dep-seed-0001 sitting on Pat's chair (tv-1vh.11) — daysAgo(10) is a free
  // day untouched by any other Pat booking.
  (['not-issued', 'draft', 'issued', 'paid', 'uninvoiced-writeoff'] as const).forEach((inv, idx) => {
    const startedAgo = daysAgo(idx === 2 ? 10 : 5 + idx);
    push({
      category: 'Invoice',
      label: inv,
      booking: mkBooking(`bk-show-invoice-${inv}`, { status: 'completed', invoiceStatus: inv, start: startedAgo, createdAt: daysAgo(15), checkedInAt: startedAgo, checkedOutAt: iso(new Date(startedAgo).getTime() + 4 * HOUR) }),
    });
  });

  // ── Reschedule history (2-entry: gap-fill + client-move) ────────────────────────
  {
    const movedTo = nextFar();
    push({
      category: 'Reschedule',
      label: '2-entry history (gap-fill + client-move)',
      booking: mkBooking('bk-show-reschedule', {
        artistId: 'brandon',
        status: 'confirmed', // a move keeps the lifecycle status (W4-D)
        start: movedTo, // sits where the last move landed
        rescheduleHistory: [
          { fromIso: farAt(0, 9), toIso: farAt(1, 14), reason: 'gap-fill', by: 'pat', at: daysAgo(9) },
          { fromIso: farAt(1, 14), toIso: movedTo, reason: 'client-move', by: 'studio-manager', at: daysAgo(3) },
        ],
      }),
    });
  }

  // ── summary vs designBrief fallback ─────────────────────────────────────────────
  push({ category: 'Summary', label: 'summary set', booking: mkBooking('bk-show-summary-ai') });
  push({
    category: 'Summary',
    label: 'designBrief fallback only',
    booking: mkBooking('bk-show-summary-brief', {
      summary: undefined,
      colour: undefined,
      styleTags: undefined,
      bodyPart: undefined,
      bodyPartSide: undefined,
      refsLocation: undefined,
      designBrief: 'Legacy freeform brief: cover-up over old script, wants heavy black to bury it, single full-day.',
    }),
  });
  push({
    category: 'Summary',
    label: 'empty (no summary, no brief)',
    booking: mkBooking('bk-show-summary-empty', {
      summary: undefined,
      colour: undefined,
      styleTags: undefined,
      bodyPart: undefined,
      bodyPartSide: undefined,
      refsLocation: undefined,
      referencePhotos: [],
      designBrief: '',
    }),
  });

  // --- Flatten ---------------------------------------------------------------
  const bookings: Booking[] = [];
  const deposits: Deposit[] = [];
  const manifest: ShowcaseEntry[] = [];
  for (const r of rows) {
    bookings.push(r.booking);
    if (r.extraBookings) bookings.push(...r.extraBookings);
    if (r.deposit) deposits.push(r.deposit);
    manifest.push({ id: r.booking.id, category: r.category, label: r.label });
  }

  return { bookings, deposits, pieces, customers, manifest };
}

/** Static manifest (id/category/label) for the /showcase screen. Timing-independent. */
export const DETAIL_SHOWCASE_MANIFEST: ShowcaseEntry[] =
  seedDetailShowcase(SHOWCASE_ANCHOR_ISO).manifest;
